/**
 * DeleteAccountSection Component
 *
 * Danger zone section for permanent account deletion.
 * Implements a two-step confirmation flow to prevent accidental deletions.
 *
 * @module pages/SettingsPage/components/DeleteAccountSection
 */

import { type ReactElement, memo, useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Group, Stack } from '@mantine/core';
import { AlertTriangle } from 'lucide-react';

import { usersService } from '@/services/api';
import { NotificationService } from '@/services/notificationService';
import { useGeoMarkupStore } from '@/store';

import styles from './DeleteAccountSection.module.css';

const WARNING_ICON_SIZE = 20;

export const DeleteAccountSection = memo(
  function DeleteAccountSection(): ReactElement {
    const { t } = useTranslation();
    const [isConfirming, setIsConfirming] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const logout = useGeoMarkupStore((state) => state.auth.logout);

    const handleStartConfirm = useCallback(() => {
      setIsConfirming(true);
    }, []);

    const handleCancel = useCallback(() => {
      setIsConfirming(false);
    }, []);

    const handleDelete = useCallback(async () => {
      setIsDeleting(true);

      try {
        await usersService.deleteAccount();

        NotificationService.showSuccess(
          t('settings.deleteAccount.success'),
          true
        );

        logout();
      } catch {
        NotificationService.showError(t('settings.deleteAccount.error'));
        setIsDeleting(false);
      }
    }, [logout, t]);

    return (
      <div className={styles.container}>
        <Stack gap="lg">
          <h3 className={styles.dangerTitle}>
            {t('settings.deleteAccount.title')}
          </h3>

          <div className={styles.warningBox}>
            <AlertTriangle
              size={WARNING_ICON_SIZE}
              className={styles.warningIcon}
            />
            <p className={styles.warningText}>
              <Trans i18nKey="settings.deleteAccount.warning" />
            </p>
          </div>

          {!isConfirming ? (
            <Button variant="outline" color="red" onClick={handleStartConfirm}>
              {t('settings.deleteAccount.deleteButton')}
            </Button>
          ) : (
            <Group>
              <Button color="red" loading={isDeleting} onClick={handleDelete}>
                {t('settings.deleteAccount.confirmButton')}
              </Button>
              <Button
                variant="subtle"
                color="gray"
                onClick={handleCancel}
                disabled={isDeleting}
              >
                {t('settings.deleteAccount.cancelButton')}
              </Button>
            </Group>
          )}
        </Stack>
      </div>
    );
  }
);
